import React, { useState, useEffect } from 'react';

interface DonationFormProps {
  formData: {
    fullName: string;
    email: string;
    idNumber: string;
    address: string;
    phone: string;
    donationAmount: string;
    termsAccepted: boolean;
    language: string;
  };
  onFormChange: (data: Partial<DonationFormProps['formData']>) => void;
  onSubmit: () => void;
  serverError?: string;
}

// Type declaration for window.MSStream
declare global {
  interface Window {
    MSStream?: any;
  }
}

// Maximum character length for inputs
const MAX_MESSAGE_LENGTH = 500;

// Translations for the form
const translations = {
  ca: {
    yourInfo: "La teva informació",
    fullName: "Nom i cognoms",
    fullNamePlaceholder: "El teu nom i cognoms",
    email: "Correu electrònic",
    emailPlaceholder: "correu@exemple.com",
    idNumber: "NIF/NIE/Passaport",
    idNumberPlaceholder: "Introdueix el teu NIF/NIE/Passaport",
    address: "Adreça postal completa",
    addressPlaceholder: "La teva adreça",
    phone: "Telèfon de contacte",
    phonePlaceholder: "El teu número de telèfon",
    donationAmount: "Import de la donació",
    chooseAmount: "Tria quant vols donar:",
    otherAmount: "Altra quantitat",
    terms: "He llegit i accepto l'avís legal i política de privacitat d'aquest web",
    requiredField: "Aquest camp és obligatori",
    invalidEmail: "Email no vàlid",
    invalidPhone: "Telèfon no vàlid",
    proceed: "Procedir al pagament",
    error: "Error:",
    mustAcceptTerms: "Has d'acceptar els termes i condicions"
  },
  es: {
    yourInfo: "Tu información",
    fullName: "Nombre y apellidos",
    fullNamePlaceholder: "Tu nombre y apellidos",
    email: "Correo electrónico",
    emailPlaceholder: "correo@ejemplo.com",
    idNumber: "NIF/NIE/Pasaporte",
    idNumberPlaceholder: "Introduce tu NIF/NIE/Pasaporte",
    address: "Dirección postal completa",
    addressPlaceholder: "Tu dirección",
    phone: "Teléfono de contacto",
    phonePlaceholder: "Tu número de teléfono",
    donationAmount: "Importe de la donación",
    chooseAmount: "Elige cuánto quieres donar:",
    otherAmount: "Otra cantidad",
    terms: "He leído y Acepto el aviso legal y política de privacidad de esta web",
    requiredField: "Este campo es obligatorio",
    invalidEmail: "Email no válido",
    invalidPhone: "Teléfono no válido",
    proceed: "Proceder al pago",
    error: "Error:",
    mustAcceptTerms: "Debes aceptar los términos y condiciones"
  },
  en: {
    yourInfo: "Your information",
    fullName: "Full name",
    fullNamePlaceholder: "Your full name",
    email: "Email address",
    emailPlaceholder: "email@example.com",
    idNumber: "ID/Passport number",
    idNumberPlaceholder: "Enter your ID number",
    address: "Full postal address",
    addressPlaceholder: "Your address",
    phone: "Contact phone",
    phonePlaceholder: "Your phone number",
    donationAmount: "Donation amount",
    chooseAmount: "Choose how much you want to donate:",
    otherAmount: "Other amount",
    terms: "I have read and accept the legal notice and privacy policy of this website",
    requiredField: "This field is required",
    invalidEmail: "Invalid email",
    invalidPhone: "Invalid phone number",
    proceed: "Proceed to payment",
    error: "Error:",
    mustAcceptTerms: "You must accept the terms and conditions"
  }
};

const DonationForm: React.FC<DonationFormProps> = ({ formData, onFormChange, onSubmit, serverError }) => {
  // Add state for field errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  // Track if user is in custom amount mode
  const [isCustomAmountMode, setIsCustomAmountMode] = useState(false);
  
  // Detect if device is iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Get the current language for translations
  const currentLang = formData.language || 'ca';
  const t = translations[currentLang as keyof typeof translations];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value, type } = e.target as HTMLInputElement;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    if (type === 'checkbox') {
      onFormChange({ [id]: checked });
    } else {
      onFormChange({ [id]: value });
    }
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [id]: true }));
    
    // Clear error when user types
    if (errors[id]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value, type } = e.target as HTMLInputElement;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setTouched(prev => ({ ...prev, [id]: true }));
    
    if (type === 'checkbox') {
      validateField(id, checked ? 'true' : 'false');
    } else {
      validateField(id, value);
    }
  };

  const validateField = (id: string, value: string) => {
    if (id === 'termsAccepted') {
      if (value !== 'true') {
        setErrors(prev => ({ ...prev, [id]: t.mustAcceptTerms }));
        return false;
      }
      return true;
    }
    
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, [id]: t.requiredField }));
      return false;
    }
    
    if (id === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors(prev => ({ ...prev, [id]: t.invalidEmail }));
        return false;
      }
    }
    
    if (id === 'phone') {
      const phoneRegex = /^[0-9+\s()-]{6,20}$/;
      if (!phoneRegex.test(value)) {
        setErrors(prev => ({ ...prev, [id]: t.invalidPhone }));
        return false;
      }
    }
    
    return true;
  };

  const handleAmountClick = (amount: string) => {
    // Exit custom amount mode when selecting a preset amount
    setIsCustomAmountMode(false);
    
    // Special handling for iOS
    if (isIOS) {
      // Focus the input first to make sure the keyboard doesn't appear
      const input = document.getElementById('donationAmount') as HTMLInputElement;
      if (input) {
        input.focus();
        // Set the amount
        onFormChange({ donationAmount: amount });
        // Blur immediately to hide keyboard
        setTimeout(() => input.blur(), 50);
      }
    } else {
      onFormChange({ donationAmount: amount });
    }
    
    // Clear any donation amount error
    if (errors.donationAmount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.donationAmount;
        return newErrors;
      });
    }
    
    // Run field validation after amount selection, but don't auto-submit
    setTimeout(() => {
      // Run validation for all fields first
      const requiredFields = [
        { id: 'fullName', message: t.requiredField },
        { id: 'email', message: t.requiredField },
        { id: 'idNumber', message: t.requiredField },
        { id: 'address', message: t.requiredField },
        { id: 'phone', message: t.requiredField },
      ];
      
      const newErrors: Record<string, string> = {};
      
      // Validate required fields
      requiredFields.forEach(field => {
        const value = formData[field.id as keyof typeof formData];
        if (!value || typeof value === 'string' && !value.trim()) {
          newErrors[field.id] = field.message;
        }
      });
      
      // Validate terms accepted
      if (!formData.termsAccepted) {
        newErrors.termsAccepted = t.mustAcceptTerms;
      }
      
      // Email validation
      if (formData.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          newErrors.email = t.invalidEmail;
        }
      }
      
      // Phone validation
      if (formData.phone) {
        const phoneRegex = /^[0-9+\s()-]{6,20}$/;
        if (!phoneRegex.test(formData.phone)) {
          newErrors.phone = t.invalidPhone;
        }
      }
      
      // Update errors state
      setErrors(newErrors);
      
      // Mark all fields as touched
      setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      
      // Removed automatic submission to prevent premature payment intent creation
    }, 100);
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-numeric characters
    const amount = e.target.value.replace(/[^\d]/g, '');
    
    // Always use the actual numeric amount the user entered
    if (amount) {
      onFormChange({ donationAmount: amount });
    } else if (isCustomAmountMode) {
      // If in custom mode but no value, keep the custom field visible
      onFormChange({ donationAmount: 'custom' });
    } else {
      onFormChange({ donationAmount: '' });
    }
    
    // Clear any donation amount error if value is not empty
    if (amount && errors.donationAmount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.donationAmount;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    
    // Validate all required fields
    const requiredFields = [
      { id: 'fullName', message: t.requiredField },
      { id: 'email', message: t.requiredField },
      { id: 'idNumber', message: t.requiredField },
      { id: 'address', message: t.requiredField },
      { id: 'phone', message: t.requiredField },
    ];
    
    requiredFields.forEach(field => {
      const value = formData[field.id as keyof typeof formData];
      if (!value || typeof value === 'string' && !value.trim()) {
        newErrors[field.id] = field.message;
        isValid = false;
      }
    });
    
    // Validate terms accepted
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = t.mustAcceptTerms;
      isValid = false;
    }
    
    // Special validation for donation amount
    if (!formData.donationAmount || formData.donationAmount === 'custom') {
      newErrors.donationAmount = t.requiredField;
      isValid = false;
    }
    
    // Email validation
    if (formData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t.invalidEmail;
        isValid = false;
      }
    }
    
    // Phone validation
    if (formData.phone) {
      const phoneRegex = /^[0-9+\s()-]{6,20}$/;
      if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = t.invalidPhone;
        isValid = false;
      }
    }
    
    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Focus the first field with an error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const input = document.getElementById(firstErrorField) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }
      return; // Prevent form submission
    }
    
    onSubmit();
  };

  // Helper function to determine input class
  const getInputClassName = (fieldName: string) => {
    return touched[fieldName] && errors[fieldName] ? 'error-input' : '';
  };

  // Only run once when component mounts
  useEffect(() => {
    // Set a default amount when the component mounts if none is set
    if (!formData.donationAmount) {
      onFormChange({ donationAmount: '50' });
    }
  }, []); // Empty dependency array - only runs once on mount

  return (
    <form id="donation-form" onSubmit={handleSubmit}>
      <div className="form-section enhanced-form-section">
        <h3 className="section-title enhanced-section-title">
          {t.yourInfo}
        </h3>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fullName" className="required-field">{t.fullName}</label>
            <input 
              type="text" 
              id="fullName" 
              placeholder={t.fullNamePlaceholder}
              value={formData.fullName || ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              maxLength={50}
              className={getInputClassName('fullName')}
            />
            {touched.fullName && errors.fullName && (
              <div className="error-message">{errors.fullName}</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="email" className="required-field">{t.email}</label>
            <input 
              type="email" 
              id="email" 
              placeholder={t.emailPlaceholder}
              required
              value={formData.email || ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={getInputClassName('email')}
            />
            {touched.email && errors.email && (
              <div className="error-message">{errors.email}</div>
            )}
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="idNumber" className="required-field">{t.idNumber}</label>
            <input 
              type="text" 
              id="idNumber" 
              placeholder={t.idNumberPlaceholder}
              required
              value={formData.idNumber || ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={getInputClassName('idNumber')}
            />
            {touched.idNumber && errors.idNumber && (
              <div className="error-message">{errors.idNumber}</div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="phone" className="required-field">{t.phone}</label>
            <input 
              type="tel" 
              id="phone" 
              placeholder={t.phonePlaceholder}
              required
              value={formData.phone || ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={getInputClassName('phone')}
            />
            {touched.phone && errors.phone && (
              <div className="error-message">{errors.phone}</div>
            )}
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="address" className="required-field">{t.address}</label>
          <input 
            id="address" 
            placeholder={t.addressPlaceholder}
            required
            value={formData.address || ''}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={getInputClassName('address')}
          />
          {touched.address && errors.address && (
            <div className="error-message">{errors.address}</div>
          )}
        </div>

        <div className="form-group terms-group">
        <div className="checkbox-group">
          <input
            type="checkbox"
            id="termsAccepted"
            checked={formData.termsAccepted || false}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
          />
          <label htmlFor="termsAccepted">{t.terms}</label>
        </div>
        {touched.termsAccepted && errors.termsAccepted && (
          <div className="error-message">{errors.termsAccepted}</div>
        )}
      </div>
      </div>

      
      <div className="form-section enhanced-form-section">
        <h3 className="section-title enhanced-section-title">
          {t.donationAmount}
        </h3>
        
        <div className="form-group">
          <label htmlFor="donationAmount" className="required-field">{t.chooseAmount}</label>
          <div className="donation-amounts">
            <button 
              type="button" 
              className={`amount-btn ${formData.donationAmount === '50' ? 'selected' : ''}`}
              onClick={() => handleAmountClick('50')}
            >
              50€
            </button>
            <button 
              type="button" 
              className={`amount-btn ${formData.donationAmount === '100' ? 'selected' : ''}`}
              onClick={() => handleAmountClick('100')}
            >
              100€
            </button>
            <button 
              type="button" 
              className={`amount-btn ${formData.donationAmount === '200' ? 'selected' : ''}`}
              onClick={() => handleAmountClick('200')}
            >
              200€
            </button>
            <button 
              type="button" 
              className={`amount-btn ${isCustomAmountMode ? 'selected' : ''}`}
              onClick={() => {
                // Don't use handleAmountClick here to avoid auto-submission
                // Enter custom amount mode
                setIsCustomAmountMode(true);
                // Set a custom value to show the input and mark this button as selected
                onFormChange({ donationAmount: 'custom' });
                
                // Focus the custom amount input when "Other amount" is clicked
                setTimeout(() => {
                  const input = document.getElementById('donationAmount') as HTMLInputElement;
                  if (input) {
                    if (isIOS) {
                      // For iOS: focus then blur to prevent keyboard
                      input.focus();
                      setTimeout(() => input.blur(), 50);
                    } else {
                      input.focus();
                    }
                  }
                }, 10);
              }}
            >
              {t.otherAmount}
            </button>
          </div>
          <div className={`custom-amount ${isCustomAmountMode ? 'visible' : 'hidden'}`}>
            <input 
              type="text" 
              id="donationAmount" 
              placeholder={t.otherAmount} 
              value={formData.donationAmount === 'custom' ? '' : formData.donationAmount || ''}
              onChange={handleAmountChange}
              onBlur={handleBlur}
              required
              className={`${formData.donationAmount && formData.donationAmount !== 'custom' ? 'selected' : ''} ${getInputClassName('donationAmount')}`}
            />
            <span className="currency">€</span>
          </div>
          <ul
              className="logos-list"
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "15px"
              }}
            >
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/Visa.svg"
                  width="35"
                  height="24"
                  alt="Visa"
                  title="Visa"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/Mastercard.svg"
                  width="35"
                  height="24"
                  alt="Mastercard"
                  title="Mastercard"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/Amex.svg"
                  width="35"
                  height="24"
                  alt="American Express"
                  title="American Express"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/PayPal.svg"
                  width="38"
                  height="24"
                  alt="Paypal"
                  title="Paypal"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
              <li>
                <img
                  className="border"
                  src="https://cdn.shopify.com/s/files/1/0638/9453/files/icon-apple.svg"
                  width="38"
                  height="24"
                  alt="Apple pay"
                  title="Apple pay"
                  loading="lazy"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid #a6a6a6",
                    borderRadius: "4px"
                  }}
                />
              </li>
            </ul>
          {touched.donationAmount && errors.donationAmount && (
            <div className="error-message">{errors.donationAmount}</div>
          )}
        </div>
      </div>
      
      {/* Show server error if present */}
      {serverError && (
        <div className="server-error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <strong>{t.error}</strong> {serverError}
          </div>
        </div>
      )}
      
      <button 
        className="submit-payment-button" 
        type="submit"
      >
        {t.proceed}
      </button>
    </form>
  );
};

export default DonationForm; 
